import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  BatteryProposalFinancingDto,
  SubmitBatteryLightReachApplicationDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

const invalidateApplicationDependents = (
  queryClient: ReturnType<typeof useQueryClient>,
  proposalId: string,
): void => {
  // The application locks the offer and the Existing Battery answer, which
  // the offers read and the proposal read report.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.lightReachOffers(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.byId(proposalId),
    exact: true,
  })
}

/**
 * Submit the seller's LightReach credit application. The response is the
 * financing read after submission. A failure may still have left a held
 * application (LightReach did not confirm), so it re-reads financing too.
 */
export default function useSubmitBatteryLightReachApplication(): UseMutationResult<
  BatteryProposalFinancingDto,
  unknown,
  { proposalId: string; body: SubmitBatteryLightReachApplicationDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      batteryProposalService.submitLightReachApplication(proposalId, body),
    // The variables carry SSNs. Drop the mutation as soon as nothing observes
    // it rather than keep it in the cache for the default five minutes.
    cacheTime: 0,
    onSuccess: (financing, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.batteryProposal.financing(proposalId),
        financing,
      )
      invalidateApplicationDependents(queryClient, proposalId)
    },
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financing(proposalId),
      })
      invalidateApplicationDependents(queryClient, proposalId)
    },
  })
}
